import React, { memo, useState, useEffect, useCallback } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Grid from '@material-ui/core/Grid'
import EditIcon from '@material-ui/icons/EditOutlined'
import { useMutation, useApolloClient } from '@apollo/react-hooks'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import CancelIcon from '@material-ui/icons/Cancel'
import classNames from 'classnames'
import AsyncSelect from 'react-select/async'

import { ISkillsProps, ISkill } from 'models'
import styles from './styles'
import { UpdateUserSkills, GetSkills } from 'store'

// const suggestions = [
// ].map(suggestion => ({
// 	value: suggestion.label,
// 	label: suggestion.label,
// }))

const useStyles = makeStyles(styles)

const NoOptionsMessage: (props: any) => JSX.Element = props => (
	<Typography
		color="textSecondary"
		className={props.selectProps.classes.noOptionsMessage}
		{...props.innerProps}
	>
		{props.children}
	</Typography>
)

const inputComponent: (props: any) => JSX.Element = ({
	inputRef,
	...props
}) => <div ref={inputRef} {...props} />

const Control: (props: any) => JSX.Element = ({
	children,
	innerProps,
	innerRef,
	selectProps: { classes, TextFieldProps },
}) => (
	<TextField
		fullWidth
		InputProps={{
			inputComponent,
			inputProps: {
				className: classes.input,
				ref: innerRef,
				children,
				...innerProps,
			},
		}}
		{...TextFieldProps}
	/>
)

const Option: (props: any) => JSX.Element = props => (
	<MenuItem
		ref={props.innerRef}
		selected={props.isFocused}
		component="div"
		style={{
			fontWeight: props.isSelected ? 500 : 400,
		}}
		{...props.innerProps}
	>
		{props.children}
	</MenuItem>
)

const Placeholder: (props: any) => JSX.Element = ({
	selectProps,
	innerProps = {},
	children,
}) => (
	<Typography
		color="textSecondary"
		className={selectProps.classes.placeholder}
		{...innerProps}
	>
		{children}
	</Typography>
)

const ValueContainer: (props: any) => JSX.Element = props => (
	<div className={props.selectProps.classes.valueContainer}>
		{props.children}
	</div>
)

const MultiValue: (props: any) => JSX.Element = props => (
	<Chip
		tabIndex={-1}
		label={props.children}
		className={classNames(props.selectProps.classes.chip, {
			[props.selectProps.classes.chipFocused]: props.isFocused,
		})}
		onDelete={props.removeProps.onClick}
		deleteIcon={<CancelIcon {...props.removeProps} />}
	/>
)

const Menu: (props: any) => JSX.Element = props => (
	<Paper
		square
		className={props.selectProps.classes.paper}
		{...props.innerProps}
	>
		{props.children}
	</Paper>
)

const components = {
	Control,
	Menu,
	MultiValue,
	NoOptionsMessage,
	Option,
	Placeholder,
	ValueContainer,
}

const Skills: React.FunctionComponent<ISkillsProps> = ({
	skills,
	canEdit,
	updateUser,
}) => {
	const client = useApolloClient()
	const [updateMutationHandle] = useMutation(UpdateUserSkills)
	const theme = useTheme()
	const [success, setSuccess] = useState(false)
	const [edit, setEdit] = useState(false)
	const [loading, setLoading] = useState(false)
	const [multi, setMulti] = React.useState(
		skills
			? skills.map((item: any) => {
					item.value = item.id
					item.label = item.title
					return item
			  })
			: [],
	)

	const loadOptions = (inputValue: any, callback: any) => {
		setTimeout(async () => {
			const { data } = await client.query({
				query: GetSkills,
				variables: {
					key: inputValue,
				},
			})
			if (data && data.searchSkills) {
				callback(
					data.searchSkills.map((item: any) => {
						item.value = item.id
						item.label = item.title
						return item
					}),
				)
			} else {
				callback([])
			}
		}, 1000)
	}

	const handleChangeMulti = useCallback(value => {
		setMulti(value)
	}, [])
	const toggle = useCallback(() => {
		setEdit(!edit)
	}, [edit])
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const isMobile = useMediaQuery('@media (max-width:680px)')
	const [displayedSkillsCount, setDisplayedSkillsCount] = useState(
		isMobile ? skills.length : 5,
	)
	const saveEdit = useCallback(() => {
		setEdit(false)
		setLoading(true)
	}, [])
	const handleViewAllSkillsClick = () => {
		setDisplayedSkillsCount(skills.length)
	}
	const displayViewAllSkills: boolean =
		displayedSkillsCount < skills.length && !isMobile
	const selectStyles = {
		input: (base: any) => ({
			...base,
			color: theme.palette.text.primary,
			'& input': {
				font: 'inherit',
			},
		}),
	}

	useEffect(() => {
		setDisplayedSkillsCount(isMobile ? skills.length : 5)
	}, [isMobile, skills])

	useEffect(() => {
		if (loading) {
			const mutate = async () => {
				try {
					await updateMutationHandle({
						variables: {
							skills: multi
								? multi.map((item: any) => item.id)
								: [],
							language: i18n.language,
						},
					})
					setLoading(false)
					setSuccess(true)
					setTimeout(() => {
						setSuccess(false)
					}, 2000)
				} catch (error) {
					setLoading(false)
				}
			}
			mutate()
			updateUser()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading])

	return (
		<Grid container={true} direction="row" style={{ position: 'relative' }}>
			{isMobile && (
				<span className={classes.skillsLabel}>{t('Skills')}: </span>
			)}
			<Grid
				container={true}
				direction="row"
				className={
					isMobile
						? classes.skillsOuterContainerMobile
						: classes.skillsOuterContainer
				}
			>
				<Grid
					container={true}
					direction="row"
					className={
						isMobile
							? classes.skillsInnerContainerMobile
							: classes.skillsInnerContainer
					}
				>
					{!isMobile && (
						<span className={classes.skillsLabel}>
							{t('Skills')}:{' '}
						</span>
					)}
					{!edit &&
						skills
							.slice(0, displayedSkillsCount)
							.map((skill: ISkill, index: number) => {
								const skillLiteral: string = `${skill.title}`

								return (
									<Chip
										key={`skill-${index}`}
										label={skillLiteral}
										className={classes.skillChip}
									/>
								)
							})}
					{displayViewAllSkills && (
						<span
							className={classes.viewAll}
							onClick={handleViewAllSkillsClick}
						>
							{t('View all')}
						</span>
					)}
				</Grid>
				{canEdit && edit && (
					<Grid container={true} direction="row">
						<AsyncSelect
							cacheOptions
							loadOptions={loadOptions}
							// onInputChange={handleInputChange}
							className={classes.skillsSelect}
							classes={classes}
							styles={selectStyles}
							inputId="react-select-multiple"
							TextFieldProps={{
								label: null,
								InputLabelProps: {
									htmlFor: 'react-select-multiple',
									shrink: true,
								},
							}}
							placeholder={t('search and select desired skills')}
							// options={suggestions}
							components={components}
							value={multi}
							onChange={handleChangeMulti}
							isMulti
						/>
					</Grid>
				)}
			</Grid>
			{canEdit && !edit && (
				<>
					{!success && (
						<Chip
							onClick={toggle}
							icon={<EditIcon />}
							variant="outlined"
							color={'secondary'}
							clickable={true}
							classes={{
								label: classes.editIconLabel,
								root: classes.editRootElement,
							}}
						/>
					)}
					{success && (
						<div className={classes.saveSuccess}>{t('saved!')}</div>
					)}
				</>
			)}
			{canEdit && edit && (
				<div className={classes.saveCancelButtons}>
					<div className={classes.cancelEdit} onClick={toggle}>
						{t('cancel')}
					</div>
					<div className={classes.saveEdit} onClick={saveEdit}>
						{t('save')}
					</div>
				</div>
			)}
		</Grid>
	)
}

export default memo(Skills)
